import React, { useEffect, useState } from 'react';
import { get, post } from 'aws-amplify/api';
import { InputGroup, FormControl, Modal, Dropdown, Badge } from 'react-bootstrap';
import { GrDocumentTxt } from 'react-icons/gr';
import { userFriendlyDateString } from '../../libs/miscLib';
import { FaCircleCheck } from 'react-icons/fa6';
import { tryToDownloadBaseWordlistFile, getCurrentWordlistObj, useSwdContext } from '../../libs/autofillLib';
import LoaderButton from '../reusable/LoaderButton';
import { useToastNotifications } from '../../libs/toastLib';



export function modsTextToModsList(modsText) {
  if (!modsText) return [];
  return modsText.split(/[\n\r]+/).map(s => s.replace(/\s/g, '')).filter(s => s !== '');
}

export function isModsTextValid(modsText) {
  if (!modsText) return true;
  const newModsList = modsTextToModsList(modsText)
  return (newModsList.length > 0 && newModsList.every(m => /^[A-Za-z]+;(?:-?\d+|-)$/.test(m)))
}

export function getInvalidLines(modsText) {
  if (!modsText) return '';
  return (modsText
    .split(/[\n\r]+/)
    .filter(s => !/^\s*$/.test(s) && !/^\s*[A-Za-z]+\s*;\s*(?:-?\d+|-)\s*$/.test(s))
    .map(s => <ul className='py-0 my-0'>{s}</ul>)
  );
}


export default function EditWordlistModal({ show, onHide, triggerRefresh, createNew=false, fromActive=false }) {

  const { postErrorNotification, removeNotificationsWithLabels } = useToastNotifications();

  const [isDownloadingBaseWordlist, setIsDownloadingBaseWordlist] = useState(false);

  // User-editable states
  const [wordlistId, setWordlistId] = useState();  // not user-editable
  const [name, setName] = useState('');
  const [author, setAuthor] = useState('');
  const [link, setLink] = useState('');
  const [description, setDescription] = useState('');
  const [baseWordlistId, setBaseWordlistId] = useState();
  const [modsText, setModsText] = useState('');     // basically list of mods joined by newlines

  const [modsTextIsValid, setModsTextIsValid] = useState(true);

  // Track the globally-active wordlist: any time it's reloaded, also update this component's state
  const { swdVersion } = useSwdContext();
  useEffect(() => {
    if (!createNew || fromActive) {
      const wl = getCurrentWordlistObj();

      // Set states, or null if wordlist is null
      setWordlistId(wl?.wordlistId);
      setName(createNew && fromActive ? `${wl?.name} (copy)` : wl?.name);
      setAuthor(wl?.author || '');
      setLink(wl?.link || '');
      setDescription(wl?.description || '');
      setBaseWordlistId(wl?.baseWordlistId);
      setModsText(wl?.mods?.join('\n') || '');
      setModsTextIsValid(true);

      setIsSaving(false);
    } else {
      // If creating a brand new wordlist, start from scratch
      setWordlistId(null);
      setName('');
      setAuthor('');
      setLink('');
      setDescription('');
      setBaseWordlistId(null);
      setModsText('');
      setModsTextIsValid(true);
      setIsSaving(false);
    }
  }, [swdVersion, createNew, fromActive]);   // reset any time swd is globally reloaded

  // Also maintain a list of the baseWordlists available so the user can pick one; refreshed onShow
  const [baseWordlists, setBaseWordlists] = useState(null);    // if null, will just display a loading message
  const refreshBaseWordlists = async () => {
    setBaseWordlists(null);    // reset
    try {
      const bwls = await (await get({ apiName: 'wordlists', path: '/baseWordlists' }).response).body.json();
      setBaseWordlists(bwls)
    } catch (e) {
      console.error(e);
    }
  };


  // Method to save new values
  const [isSaving, setIsSaving] = useState(false);
  async function save() {
    if (!modsTextIsValid || !baseWordlistId) return;

    setIsSaving(true);
    removeNotificationsWithLabels('edit-wordlist-err');
    try {
      await (
        await post({
          apiName: 'wordlists',
          path: createNew ? '/wordlists' : `/wordlists/${wordlistId}`,
          options: {
            body: {
              name,
              author,
              description,
              link,
              baseWordlistId,
              mods: modsTextToModsList(modsText),
            },
          },
        }).response
      ).body.json();

      // Close modal and trigger refresh
      if (triggerRefresh) triggerRefresh();
      setIsSaving(false);
      onHide();
    } catch (e) {
      setIsSaving(false);
      console.log(e);
      postErrorNotification('Unable to save your wordlist changes.', 'Try again later, and let us know if this persists.', 'edit-wordlist-err')
    }
  }


  return (
    <Modal show={show} onHide={onHide} onShow={() => refreshBaseWordlists()}>
      <Modal.Header className='fw-bold purple-1-bkgd' closeButton>
        {name || '(untitled wordlist)'}
      </Modal.Header>
      <Modal.Body>
      
        <InputGroup className='mb-3'>
          <InputGroup.Text id='wordlist-name'>Name</InputGroup.Text>
          <FormControl
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder='Enter wordlist name'
            aria-label='Name'
            aria-describedby='wordlist-name'
          />
        </InputGroup>
        <InputGroup className='mb-2' size='sm'>
          <InputGroup.Text id='wordlist-author'>Author</InputGroup.Text>
          <FormControl
            value={author}
            onChange={e => setAuthor(e.target.value)}
            placeholder='(optional)'
            aria-label='Author'
            aria-describedby='wordlist-author'
          />
        </InputGroup>
        <InputGroup className='mb-2' size='sm'>
          <InputGroup.Text id='wordlist-link'>Website</InputGroup.Text>
          <FormControl
            value={link}
            onChange={e => setLink(e.target.value)}
            placeholder='(optional)'
            aria-label='Link'
            aria-describedby='wordlist-link'
          />
        </InputGroup>
        <InputGroup className='mb-3' size='sm'>
          <InputGroup.Text id='wordlist-description'>Description</InputGroup.Text>
          <FormControl
            as='textarea'
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder='(optional)'
            aria-label='Description'
            aria-describedby='wordlist-description'
          />
        </InputGroup>

        <div className='my-3'>
          <div className='h6 fw-bold'>Base file</div>
          <div className='d-flex gap-2 align-items-center text-success my-3'>
            <div className='d-flex align-items-center gap-1 px-1 py-2 border border-success rounded-1'>
              <GrDocumentTxt />
              <span className='text-xs'>{baseWordlists?.find(bwl => bwl.baseWordlistId === baseWordlistId)?.filename}</span>
            </div>
            {baseWordlistId && (
              <LoaderButton
                variant='link'
                size='sm'
                isLoading={isDownloadingBaseWordlist}
                onClick={async () => {
                  setIsDownloadingBaseWordlist(true);
                  await tryToDownloadBaseWordlistFile(baseWordlistId, baseWordlists?.find(bwl => bwl.baseWordlistId === baseWordlistId)?.filename);
                  setIsDownloadingBaseWordlist(false);
                }}
              >Download</LoaderButton>
            )}
            {baseWordlists && (
              <Dropdown>
                <Dropdown.Toggle size='sm' variant='outline-primary'>{baseWordlistId ? 'Switch' : 'Select'} base file</Dropdown.Toggle>
                <Dropdown.Menu>
                  {baseWordlists.toSorted((a, b) => b.createdAt - a.createdAt).map((bwl, i) => (
                    <Dropdown.Item className='d-flex gap-2 align-items-baseline' key={`bwl-drop-${i}`} onClick={() => setBaseWordlistId(bwl.baseWordlistId)}>
                      {bwl.baseWordlistId === baseWordlistId && <FaCircleCheck className='text-success' />}
                      {bwl.filename}
                      {bwl.isCommunity && <Badge bg='success'>COMMUNITY</Badge>}
                      <span className='text-muted text-xs ms-auto'>{userFriendlyDateString(bwl.createdAt)}</span>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        </div>

        <div>
          <div className='h6 fw-bold'>Modifications</div>

          <div className='d-flex gap-2'>
            <div className='d-flex flex-column flex-grow-1'>
              <FormControl
                className='block-text'
                style={{ lineHeight: '1.04rem' }}
                as='textarea'
                size='sm'
                rows={11}
                value={modsText || ''}
                onChange={e => {
                  setModsText(e.target.value);
                  setModsTextIsValid(isModsTextValid(e.target.value));
                }}
                isInvalid={!modsTextIsValid}
                placeholder={'GoodWord;50\nMehWord;30\nTerribleWord;-' /* need {} for \n escape */}
                spellCheck={false}
              />
              <FormControl.Feedback type='invalid'>
                Invalid lines:
                <li className='list-unstyled block-text'>
                  {getInvalidLines(modsText)}
                </li>
              </FormControl.Feedback>
            </div>

            <div className='small mt-1' style={{ maxWidth: '120px' }}>
              <div className='text-nowrap'>Each line:</div>
              <div className='text-nowrap block-text fw-bold'>[WORD];[SCORE]</div>
              <div className='text-muted text-xs'>
                Letter case doesn't matter. Spaces &amp; tabs are allowed and ignored.
              </div>
            </div>
          </div>


          <div className='text-end'>
            <LoaderButton
              className='mt-3'
              variant='success'
              disabled={!modsTextIsValid || !baseWordlistId || !name}
              isLoading={isSaving}
              onClick={() => save()}
            >
              Save
            </LoaderButton>
          </div>
        </div>

      </Modal.Body>
    </Modal>
  );
}